import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLazyGeSuggestionQuery } from "@/pages/Private/redux/liPeople/liPeople.api";
import {
	useLazyGetListRecommendationsQuery,
	useLazyGetOnboardingIcpQuery,
} from "@/pages/Private/redux/onboardingIcp/onboardingIcp.api";

import { ListRecommendations } from "../../schema/icp";
import { defaultConfigFor, ListPrefill } from "./onboardingListDefaults";
import {
	FALLBACK_FEATURED_KEYS,
	JobLocation,
	ONBOARDING_LISTS_MAX,
	OnboardingListConfig,
	OnboardingListTypeKey,
} from "./onboardingListTypes";
import { ConfigFieldErrors, configErrorsFor } from "./onboardingListValidation";
import { resolveIcpLocation } from "./resolveIcpLocation";
import { OnboardingFilterOptions, resolveIndustries } from "./configs/useOnboardingFilterOptions";

type Selections = Partial<Record<OnboardingListTypeKey, OnboardingListConfig>>;

/** A lookup that has finished — `value` may still be `null` when it failed. */
interface Loaded<T> {
	value: T;
}

/**
 * How long the rows may stay hidden waiting for the recommendation. Past it the
 * picker seeds from whatever arrived, so a hung AI call shows a fallback list
 * instead of an endless spinner.
 */
export const PREFILL_TIMEOUT_MS = 25000;

export interface OnboardingListPicker {
	featuredKeys: OnboardingListTypeKey[];
	selections: Selections;
	selectedKeys: OnboardingListTypeKey[];
	errorsByKey: Partial<Record<OnboardingListTypeKey, ConfigFieldErrors>>;
	allComplete: boolean;
	/** False until the recommendation + ICP prefill has been applied to the rows. */
	isReady: boolean;
	/** The recommendation call came back empty — the rows hold the blank fallback. */
	recommendationFailed: boolean;
	/** A recommendation arrived late and is waiting, because the user is mid-edit. */
	hasUnappliedRecommendation: boolean;
	isRetryingRecommendation: boolean;
	/** Applies the waiting recommendation, replacing what the rows hold. */
	applyRecommendation: () => void;
	retryRecommendation: () => Promise<void>;
	toggleType: (key: OnboardingListTypeKey) => void;
	updateConfig: (key: OnboardingListTypeKey, config: OnboardingListConfig) => void;
}

/** The two types the picker features — the recommended pair, or the fallback. */
const featuredFrom = (recommendations: ListRecommendations | null): OnboardingListTypeKey[] => {
	const recommended = recommendations?.types;

	return recommended?.length === 2 ? [...recommended] : [...FALLBACK_FEATURED_KEYS];
};

/**
 * Owns the picker's state: it loads the ICP-based recommendation, prefills the
 * two recommended types once the industry lookup can be applied, and validates
 * every selected config on each change.
 */
export const useOnboardingListPicker = (
	options: OnboardingFilterOptions,
	onMaxReached: () => void
): OnboardingListPicker => {
	const [fetchRecommendations] = useLazyGetListRecommendationsQuery();
	const [fetchIcp] = useLazyGetOnboardingIcpQuery();
	const [fetchLocationSuggestions] = useLazyGeSuggestionQuery();

	const [recommendations, setRecommendations] = useState<Loaded<ListRecommendations | null> | null>(
		null
	);
	const [icpLocation, setIcpLocation] = useState<Loaded<JobLocation | null> | null>(null);
	const [hasTimedOut, setHasTimedOut] = useState(false);
	const [featuredKeys, setFeaturedKeys] = useState<OnboardingListTypeKey[]>(FALLBACK_FEATURED_KEYS);
	const [selections, setSelections] = useState<Selections>({});
	const [isSeeded, setIsSeeded] = useState(false);
	const [hasEdited, setHasEdited] = useState(false);
	const [hasUnappliedRecommendation, setHasUnappliedRecommendation] = useState(false);
	const [isRetryingRecommendation, setIsRetryingRecommendation] = useState(false);
	const bootedRef = useRef(false);
	const seededRef = useRef(false);
	/** Whether the rows already hold a real recommendation, rather than the fallback. */
	const appliedRecommendationRef = useRef(false);

	useEffect(() => {
		if (bootedRef.current) {
			return;
		}

		bootedRef.current = true;

		// The hiring list cannot run without a location, so seed it from the ICP
		// the user already confirmed instead of leaving a blocking blank. It runs
		// next to the recommendation, not after it — the rows wait for the slower
		// of the two, never for their sum.
		const loadIcpLocation = async () => {
			try {
				// preferCacheValue: the chat prewarmed both the ICP and this
				// location suggestion on its way here (`useOnboardingListsPrewarm`).
				const icp = await fetchIcp(undefined, true).unwrap();

				setIcpLocation({ value: await resolveIcpLocation(icp.location, fetchLocationSuggestions) });
			} catch (err) {
				console.error(err);
				setIcpLocation({ value: null });
			}
		};

		const loadRecommendations = async () => {
			try {
				// preferCacheValue: the chat already fetched (and previewed) the
				// recommendation — reuse it instead of paying for a second AI call
				// that could pick different types than the preview promised.
				setRecommendations({ value: await fetchRecommendations(undefined, true).unwrap() });
			} catch (err) {
				console.error(err);
				setRecommendations({ value: null });
			}
		};

		loadIcpLocation();
		loadRecommendations();

		const timer = setTimeout(() => setHasTimedOut(true), PREFILL_TIMEOUT_MS);

		return () => clearTimeout(timer);
	}, [fetchIcp, fetchLocationSuggestions, fetchRecommendations]);

	const seedRows = useCallback((featured: OnboardingListTypeKey[], rowPrefill: ListPrefill) => {
		setFeaturedKeys(featured);
		setSelections({
			[featured[0]]: defaultConfigFor(featured[0], rowPrefill),
			[featured[1]]: defaultConfigFor(featured[1], rowPrefill),
		});
	}, []);

	const prefill: ListPrefill = useMemo(
		() => ({
			recommendations: recommendations?.value ?? null,
			icpLocation: icpLocation?.value ?? null,
			industries: resolveIndustries(
				recommendations?.value?.industries ?? [],
				options.industriesByName
			),
		}),
		[recommendations, icpLocation, options.industriesByName]
	);

	// Seeding waits for the industry lookup: a recommended industry can only be
	// applied as a filter once its NAICS codes are known. The resolved location
	// only holds it up when a featured row is the one type that needs it.
	useEffect(() => {
		const featured = featuredFrom(recommendations?.value ?? null);
		const awaitsLocation = featured.includes("hiring") && !icpLocation;
		const isPrefilled = recommendations && options.areIndustriesReady && !awaitsLocation;

		if (seededRef.current || !(isPrefilled || hasTimedOut)) {
			return;
		}

		seededRef.current = true;
		// Seeding because the timeout fired means the recommendation was not there to
		// seed from, which is what leaves the door open for the late-arrival effect.
		appliedRecommendationRef.current = Boolean(recommendations?.value);

		seedRows(featured, prefill);
		setIsSeeded(true);
	}, [recommendations, icpLocation, hasTimedOut, options.areIndustriesReady, prefill, seedRows]);

	/**
	 * A recommendation that lands after the timeout is still worth having — the only
	 * alternative is the blank fallback form for the rest of onboarding.
	 *
	 * It is applied **only** while the user has not started on the rows. Swapping the
	 * featured types and every prefilled field under someone mid-edit is PREC-3719, the
	 * regression that made this step hide its rows until seeded in the first place; once
	 * they have touched anything it is offered instead of taken.
	 */
	useEffect(() => {
		const recommended = recommendations?.value;

		if (!isSeeded || !recommended || appliedRecommendationRef.current) {
			return;
		}

		if (!options.areIndustriesReady) {
			return;
		}

		appliedRecommendationRef.current = true;

		if (hasEdited) {
			setHasUnappliedRecommendation(true);

			return;
		}

		seedRows(featuredFrom(recommended), prefill);
	}, [isSeeded, recommendations, options.areIndustriesReady, hasEdited, prefill, seedRows]);

	const applyRecommendation = () => {
		const recommended = recommendations?.value;

		if (!recommended) {
			return;
		}

		seedRows(featuredFrom(recommended), prefill);
		setHasUnappliedRecommendation(false);
	};

	/**
	 * Re-asks for the recommendation, ignoring the cached miss the first attempt left.
	 * A failed call is not fatal — the rows already hold the fallback — so this only
	 * ever improves them.
	 */
	const retryRecommendation = async () => {
		setIsRetryingRecommendation(true);
		appliedRecommendationRef.current = false;

		try {
			setRecommendations({ value: await fetchRecommendations(undefined, false).unwrap() });
		} catch (err) {
			console.error(err);
			setRecommendations({ value: null });
		} finally {
			setIsRetryingRecommendation(false);
		}
	};

	const toggleType = (key: OnboardingListTypeKey) => {
		setHasEdited(true);
		setSelections((previous) => {
			if (previous[key]) {
				const next = { ...previous };

				delete next[key];

				return next;
			}

			if (Object.keys(previous).length >= ONBOARDING_LISTS_MAX) {
				onMaxReached();

				return previous;
			}

			return { ...previous, [key]: defaultConfigFor(key, prefill) };
		});
	};

	const updateConfig = (key: OnboardingListTypeKey, config: OnboardingListConfig) => {
		setHasEdited(true);
		setSelections((previous) => ({ ...previous, [key]: config }));
	};

	const selectedKeys = Object.keys(selections) as OnboardingListTypeKey[];

	const errorsByKey = selectedKeys.reduce<
		Partial<Record<OnboardingListTypeKey, ConfigFieldErrors>>
	>((acc, key) => {
		const config = selections[key];

		if (config) {
			acc[key] = configErrorsFor(config);
		}

		return acc;
	}, {});

	return {
		featuredKeys,
		selections,
		selectedKeys,
		errorsByKey,
		allComplete:
			selectedKeys.length > 0 &&
			selectedKeys.every((key) => Object.keys(errorsByKey[key] ?? {}).length === 0),
		isReady: isSeeded,
		// Only a call that came back counts as failed — one still in flight may yet land,
		// and the late-arrival effect above is waiting for it.
		recommendationFailed: recommendations?.value === null && !isRetryingRecommendation,
		hasUnappliedRecommendation,
		isRetryingRecommendation,
		applyRecommendation,
		retryRecommendation,
		toggleType,
		updateConfig,
	};
};
